import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function LPTooltip({ tooltipText }) {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [flip, setFlip] = useState(false);

  // compute flip whenever we open or window resizes
  const computeFlip = () => {
    if (!triggerRef.current) return;
    const { left, width } = triggerRef.current.getBoundingClientRect();
    // if our trigger is past halfway, or too close to right edge for our tooltip width
    setFlip(left + width / 2 > window.innerWidth / 2);
  };

  useEffect(() => {
    if (isOpen) computeFlip();
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('resize', computeFlip);
    return () => window.removeEventListener('resize', computeFlip);
  }, []);

  // position the portal relative to trigger
  const [portalStyle, setPortalStyle] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = Math.min(240, window.innerWidth * 0.45);
    const top = rect.top + window.scrollY - 8; // 8px gap
    let left = flip
      ? rect.right + window.scrollX - tooltipWidth
      : rect.left + window.scrollX;
    // clamp
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
    setPortalStyle({ top, left, width: tooltipWidth });
  }, [isOpen, flip]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="cursor-pointer flex h-5 w-5 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-surface)] text-white"
      >
        <span className="text-xs">?</span>
      </div>

      {isOpen &&
        createPortal(
          <div
            style={{ 
              position: 'absolute',
              top: portalStyle.top, // 8px gap
              left: flip ? portalStyle.left - 28 : portalStyle.left + 28,
              width: portalStyle.width,
              zIndex: 9999,
            }}
            className="
              bg-[var(--bg-surface-variant)] border border-[var(--border)]
              rounded-sm p-2 text-xs text-white
              whitespace-normal break-words shadow-lg
              transition-opacity duration-200 ease-out
            "
          >
            {tooltipText}
          </div>,
          document.body // ← make sure to pass the container!
        )}
    </>
  );
}
